/**
 * Mood faces in and out of a GLB — ADR-043 M0, the carrier.
 *
 * A mood is a whole-material variant (`KHR_materials_variants`) that changes
 * ONLY the base colour: a clone of the primitive's own material whose
 * `baseColorTexture` points at another image (ADR-043 D1). three's
 * GLTFLoader ignores the extension and GLTFExporter writes only the scene's
 * materials, so before this module an exported or packaged body silently kept
 * its default face and dropped the rest. That silent strip is the deleted path.
 *
 * Two pure halves, both over GLB bytes:
 *  - {@link readMoodVariants} reads a file's moods, keyed by the BASE
 *    material's NAME (the identity Harry keeps everywhere; GLTFExporter
 *    renumbers materials and primitives, so indices are never carried). It
 *    REFUSES what it cannot carry whole — a variant that changes anything but
 *    the base colour, primitives wearing one material with different mappings
 *    — so a file is never half-carried.
 *  - {@link applyMoodVariants} re-applies them to an exported GLB, BEFORE the
 *    derive (ADR-020), so mood images ride the same delivery ceiling as the
 *    base colour they replace. It refuses a base material the export lacks or
 *    names twice.
 *
 * The base mood is carried exactly as the file had it: when the file maps its
 * default material to a named variant (the guard: `asleep`), that mapping is
 * written back, so World can select the base by name (Codex P2, #257).
 *
 * Depends on: glb-container, errors. Depended on by: the app's export door and
 * model import (refusal at load).
 */
import { buildGlb, imageBytes, parseGlbContainer, type GlbJson } from './glb-container.js';
import { TextureMasterError } from './errors.js';

export const KHR_MATERIALS_VARIANTS = 'KHR_materials_variants';

/** One mood of one base material: the variant names it answers to and its base-colour image. */
export interface MoodImage {
  readonly variants: readonly string[];
  readonly image: Uint8Array;
  readonly mimeType: string;
}

/** Every mood a file declares for one base material. */
export interface MoodBase {
  /** The default material's NAME — the identity the export is matched on. */
  readonly material: string;
  /** Variant names mapped explicitly to the default material itself (the base mood). */
  readonly baseVariants: readonly string[];
  readonly moods: readonly MoodImage[];
}

export interface MoodSet {
  /** The file's variant names, in its own order. */
  readonly variants: readonly string[];
  readonly bases: readonly MoodBase[];
}

type Material = NonNullable<GlbJson['materials']>[number];
type Texture = NonNullable<GlbJson['textures']>[number];
interface Mapping {
  readonly material: number;
  readonly variants: readonly number[];
}
interface Primitive {
  material?: number;
  extensions?: Record<string, unknown>;
  [key: string]: unknown;
}
interface Mesh {
  name?: string;
  primitives: Primitive[];
}

/** The name Harry's loader gives a material — authored, or minted from its index (load-glb.ts). */
const materialName = (material: Material | undefined, index: number): string =>
  typeof material?.name === 'string' && material.name !== '' ? material.name : `material_${index}`;

/** Stable JSON (sorted keys), for "equal except the fields a mood may change". */
function canonical(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonical).join(',')}]`;
  if (value !== null && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, v]) => v !== undefined)
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
    return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${canonical(v)}`).join(',')}}`;
  }
  return JSON.stringify(value);
}

/**
 * A texture reference with glTF's defaults filled in (texCoord 0, scale 1,
 * strength 1), so a file that writes a default and one that omits it compare
 * equal (Codex P2, #259).
 */
function effectiveInfo(info: Record<string, unknown> | undefined, extra: Record<string, unknown> = {}): unknown {
  if (info === undefined) return null;
  return withEffectiveTransform({ texCoord: 0, ...extra, ...info });
}

/** KHR_texture_transform's own defaults written out: offset [0,0], rotation 0, scale [1,1] (Codex P2, #259). */
function withEffectiveTransform(info: Record<string, unknown>): Record<string, unknown> {
  const ext = info['extensions'] as Record<string, unknown> | undefined;
  const t = ext?.['KHR_texture_transform'] as Record<string, unknown> | undefined;
  if (t === undefined) return info;
  return { ...info, extensions: { ...ext, KHR_texture_transform: { offset: [0, 0], rotation: 0, scale: [1, 1], ...t } } };
}

/**
 * A material's EFFECTIVE values, minus the fields a mood may change (its name,
 * its base-colour texture index): every glTF default written out, so writing
 * `metallicFactor: 1` and omitting it are the same material.
 */
function withoutMoodFields(material: Material): unknown {
  const m = material as Record<string, unknown> & { pbrMetallicRoughness?: Record<string, unknown> };
  const pbr = m.pbrMetallicRoughness ?? {};
  const base = pbr['baseColorTexture'] as Record<string, unknown> | undefined;
  const { index: _i, ...baseRest } = base ?? { index: 0 };
  void _i;
  return {
    pbr: {
      baseColorFactor: pbr['baseColorFactor'] ?? [1, 1, 1, 1],
      metallicFactor: pbr['metallicFactor'] ?? 1,
      roughnessFactor: pbr['roughnessFactor'] ?? 1,
      baseColorTexture: base === undefined ? null : withEffectiveTransform({ texCoord: 0, ...baseRest }),
      metallicRoughnessTexture: effectiveInfo(pbr['metallicRoughnessTexture'] as Record<string, unknown> | undefined),
      extensions: pbr['extensions'] ?? null,
    },
    normalTexture: effectiveInfo(m['normalTexture'] as Record<string, unknown> | undefined, { scale: 1 }),
    occlusionTexture: effectiveInfo(m['occlusionTexture'] as Record<string, unknown> | undefined, { strength: 1 }),
    emissiveTexture: effectiveInfo(m['emissiveTexture'] as Record<string, unknown> | undefined),
    emissiveFactor: m['emissiveFactor'] ?? [0, 0, 0],
    alphaMode: m['alphaMode'] ?? 'OPAQUE',
    alphaCutoff: (m['alphaMode'] ?? 'OPAQUE') === 'MASK' ? (m['alphaCutoff'] ?? 0.5) : null,
    doubleSided: m['doubleSided'] ?? false,
    extensions: m['extensions'] ?? null,
  };
}

/**
 * What decides how a texture SAMPLES its image: the sampler's settings
 * (resolved, so two samplers with equal settings agree) and the texture's
 * extensions. Its `source` is the image a mood swaps, and its `name` is a
 * label (Tripo names its textures; a cloned mood texture often has none —
 * the guard, 2026-10-13).
 */
const samplingOf = (json: GlbJson, texture: Texture | undefined): unknown => {
  if (texture === undefined) return undefined;
  const samplers = (json as { samplers?: Record<string, unknown>[] }).samplers ?? [];
  const s = texture.sampler === undefined ? {} : samplers[texture.sampler];
  if (s === undefined) return `missing sampler ${texture.sampler}`;
  // The sampler's EFFECTIVE behaviour (glTF defaults: wrap REPEAT, filters
  // implementation-chosen); its name and extras are labels (Codex P2, #259).
  return {
    magFilter: s['magFilter'] ?? null,
    minFilter: s['minFilter'] ?? null,
    wrapS: s['wrapS'] ?? 10497,
    wrapT: s['wrapT'] ?? 10497,
    samplerExtensions: s['extensions'] ?? null,
    extensions: texture.extensions ?? null,
  };
};

function meshesOf(json: GlbJson): Mesh[] {
  return ((json as { meshes?: Mesh[] }).meshes ?? []) as Mesh[];
}

function mappingsOf(primitive: Primitive): Mapping[] | null {
  const ext = primitive.extensions?.[KHR_MATERIALS_VARIANTS] as { mappings?: Mapping[] } | undefined;
  return ext?.mappings ?? null;
}

/**
 * The moods a GLB declares, or null when it declares none. Refuses — with
 * every reason listed — a file whose variants this carrier cannot keep whole.
 */
export function readMoodVariants(glb: ArrayBuffer): MoodSet | null {
  const container = parseGlbContainer(glb);
  const { json } = container;
  const root = (json.extensions as Record<string, unknown> | undefined)?.[KHR_MATERIALS_VARIANTS] as
    | { variants?: { name?: string }[] }
    | undefined;
  const meshes = meshesOf(json);
  const mapped = meshes.some((m) => m.primitives.some((p) => mappingsOf(p) !== null));
  if (root === undefined && !mapped) return null;

  const issues: string[] = [];
  const variantNames = (root?.variants ?? []).map((v, i) => (typeof v.name === 'string' && v.name !== '' ? v.name : `variant_${i}`));
  if (variantNames.length === 0) issues.push(`${KHR_MATERIALS_VARIANTS} declares no variants`);
  if (new Set(variantNames).size !== variantNames.length) issues.push(`${KHR_MATERIALS_VARIANTS} names a variant twice: ${variantNames.join(', ')}`);

  const materials = json.materials ?? [];
  const textures = json.textures ?? [];
  const names = materials.map(materialName);
  // Per default material: the canonical form of its mapping list, so primitives that disagree are caught.
  const byBase = new Map<number, { key: string; mappings: Mapping[] }>();

  // Materials worn by at least one primitive WITHOUT mappings: moods are
  // carried per material (the export renumbers primitives), so a material
  // worn both with and without moods cannot be carried exactly (Codex P2, #259).
  const wornBare = new Set<number>();
  for (const mesh of meshes) for (const p of mesh.primitives) if (mappingsOf(p) === null && p.material !== undefined) wornBare.add(p.material);
  meshes.forEach((mesh, mi) => {
    mesh.primitives.forEach((primitive, pi) => {
      const mappings = mappingsOf(primitive);
      if (mappings === null) return;
      const where = `mesh "${mesh.name ?? mi}" primitive ${pi}`;
      const base = primitive.material;
      if (base === undefined || materials[base] === undefined) {
        issues.push(`${where} maps variants but wears no material of its own — nothing to clone a mood from`);
        return;
      }
      for (const m of mappings) {
        if (materials[m.material] === undefined) issues.push(`${where} maps variants to material ${m.material}, which does not exist`);
        for (const v of m.variants) if (variantNames[v] === undefined) issues.push(`${where} maps variant ${v}, which is not declared`);
      }
      // KHR_materials_variants looks a variant up by membership, not position:
      // compare the variant → material RELATION, sorted (Codex P2, #259).
      const key = canonical(mappings.flatMap((m) => m.variants.map((v) => `${v}->${m.material}`)).sort());
      const seen = byBase.get(base);
      if (seen === undefined) byBase.set(base, { key, mappings });
      else if (seen.key !== key) {
        issues.push(`material "${names[base]}" is worn by primitives with DIFFERENT mood mappings — a mood is carried per material, so they must agree`);
      }
    });
  });

  const bases: MoodBase[] = [];
  for (const [base, { mappings }] of byBase) {
    const baseMaterial = materials[base] as Material;
    const baseName = names[base] as string;
    if (wornBare.has(base)) {
      issues.push(`material "${baseName}" is worn by primitives with moods AND without — moods are carried per material, so every wearer must declare them`);
      continue;
    }
    if (names.filter((n) => n === baseName).length > 1) {
      issues.push(`material name "${baseName}" is used twice — moods are matched by name, so it must be unique`);
      continue;
    }
    const baseTexIndex = baseMaterial.pbrMetallicRoughness?.baseColorTexture?.index;
    const baseVariants: string[] = [];
    const moods: MoodImage[] = [];
    for (const m of mappings) {
      const variants = m.variants.map((v) => variantNames[v] ?? `variant_${v}`);
      if (m.material === base) {
        baseVariants.push(...variants);
        continue;
      }
      const moodMaterial = materials[m.material];
      if (moodMaterial === undefined) continue; // listed above
      const label = `mood ${variants.join('/')} of "${baseName}"`;
      if (canonical(withoutMoodFields(moodMaterial)) !== canonical(withoutMoodFields(baseMaterial))) {
        issues.push(`${label} changes more than the base colour (material "${materialName(moodMaterial, m.material)}") — Harry carries base-colour moods only (ADR-043 D1)`);
        continue;
      }
      const texIndex = moodMaterial.pbrMetallicRoughness?.baseColorTexture?.index;
      if (texIndex === undefined || baseTexIndex === undefined) {
        issues.push(`${label}: both the mood and its base need a base-colour texture`);
        continue;
      }
      if (canonical(samplingOf(json, textures[texIndex])) !== canonical(samplingOf(json, textures[baseTexIndex]))) {
        issues.push(`${label} samples its image differently from the base (texture ${texIndex} vs ${baseTexIndex}) — only the image may change`);
        continue;
      }
      const source = textures[texIndex]?.source;
      const image = source === undefined ? undefined : json.images?.[source];
      if (source === undefined || image === undefined) {
        issues.push(`${label}: texture ${texIndex} has no image`);
        continue;
      }
      try {
        const bytes = imageBytes(container, source);
        moods.push({ variants, image: bytes.slice(), mimeType: image.mimeType ?? 'image/png' });
      } catch (err) {
        if (!(err instanceof TextureMasterError)) throw err;
        issues.push(...err.reasons.map((r) => `${label}: ${r}`));
      }
    }
    bases.push({ material: baseName, baseVariants, moods });
  }
  if (issues.length > 0) throw new TextureMasterError(issues);
  return { variants: variantNames, bases };
}

const align4 = (n: number): number => (n + 3) & ~3;

/**
 * Re-apply a {@link MoodSet} to an exported GLB that carries none. Each mood
 * becomes a clone of its base material (matched by NAME) with only the
 * base-colour texture's image swapped; every primitive wearing that base gets
 * the mappings, the base mood's own name included.
 */
export interface AppliedMoods {
  readonly glb: ArrayBuffer;
  /** Each clone written, by the base it was cloned from (callers mirror per-material requests onto them). */
  readonly clones: readonly { readonly base: string; readonly material: string }[];
}

export function applyMoodVariants(glb: ArrayBuffer, set: MoodSet): AppliedMoods {
  const container = parseGlbContainer(glb);
  const json: GlbJson = structuredClone(container.json);
  const issues: string[] = [];
  if ((json.extensions as Record<string, unknown> | undefined)?.[KHR_MATERIALS_VARIANTS] !== undefined) {
    throw new TextureMasterError([`the export already declares ${KHR_MATERIALS_VARIANTS} — refusing to merge two mood sets`]);
  }
  const variantIndex = new Map(set.variants.map((name, i) => [name, i]));
  const indexOf = (names: readonly string[]): number[] =>
    names.map((n) => {
      const i = variantIndex.get(n);
      if (i === undefined) issues.push(`mood variant "${n}" is not in the set's variant list`);
      return i ?? -1;
    });

  const materials = (json.materials ??= []);
  const textures = (json.textures ??= []);
  const images = (json.images ??= []);
  const bufferViews = (json.bufferViews ??= []);
  const names = materials.map(materialName);
  const meshes = meshesOf(json);
  const appended: Uint8Array[] = [];
  const clones: { base: string; material: string }[] = [];
  let cursor = align4(container.bin.byteLength);

  for (const base of set.bases) {
    const matches = names.flatMap((n, i) => (n === base.material ? [i] : []));
    if (matches.length !== 1) {
      issues.push(
        matches.length === 0
          ? `the export has no material "${base.material}" — its moods (${base.moods.map((m) => m.variants.join('/')).join(', ')}) have nothing to attach to`
          : `the export names material "${base.material}" ${matches.length} times — moods are matched by name`,
      );
      continue;
    }
    const baseIndex = matches[0] as number;
    const baseMaterial = materials[baseIndex] as Material;
    const baseInfo = baseMaterial.pbrMetallicRoughness?.baseColorTexture;
    const baseTexture = baseInfo === undefined ? undefined : textures[baseInfo.index];
    if (baseInfo === undefined || baseTexture === undefined) {
      issues.push(`the export's material "${base.material}" lost its base-colour texture — a mood has nothing to swap`);
      continue;
    }
    const wearers = meshes.flatMap((mesh) => mesh.primitives.filter((p) => p.material === baseIndex));
    if (wearers.length === 0) {
      issues.push(`nothing in the export wears material "${base.material}" — its moods would never show`);
      continue;
    }
    const mappings: Mapping[] = base.baseVariants.length > 0 ? [{ material: baseIndex, variants: indexOf(base.baseVariants) }] : [];
    for (const mood of base.moods) {
      appended.push(mood.image);
      bufferViews.push({ buffer: 0, byteOffset: cursor, byteLength: mood.image.byteLength });
      cursor = align4(cursor + mood.image.byteLength);
      const tag = mood.variants.join('+');
      images.push({ bufferView: bufferViews.length - 1, mimeType: mood.mimeType, name: `${base.material}__${tag}` });
      textures.push({ ...structuredClone(baseTexture), source: images.length - 1 });
      let cloneName = `${base.material}__${tag}`;
      while (names.includes(cloneName)) cloneName = `${cloneName}_`;
      names.push(cloneName);
      const clone = structuredClone(baseMaterial);
      clone.name = cloneName;
      clone.pbrMetallicRoughness = { ...clone.pbrMetallicRoughness, baseColorTexture: { ...baseInfo, index: textures.length - 1 } };
      materials.push(clone);
      clones.push({ base: base.material, material: cloneName });
      mappings.push({ material: materials.length - 1, variants: indexOf(mood.variants) });
    }
    for (const primitive of wearers) {
      primitive.extensions = { ...(primitive.extensions ?? {}), [KHR_MATERIALS_VARIANTS]: { mappings } };
    }
  }
  if (issues.length > 0) throw new TextureMasterError(issues);

  json.extensions = { ...((json.extensions as Record<string, unknown> | undefined) ?? {}), [KHR_MATERIALS_VARIANTS]: { variants: set.variants.map((name) => ({ name })) } };
  const used = new Set((json.extensionsUsed as string[] | undefined) ?? []);
  used.add(KHR_MATERIALS_VARIANTS);
  json.extensionsUsed = [...used];

  const bin = new Uint8Array(cursor);
  bin.set(container.bin, 0);
  let at = align4(container.bin.byteLength);
  for (const bytes of appended) {
    bin.set(bytes, at);
    at = align4(at + bytes.byteLength);
  }
  const buffers = json.buffers ?? [];
  json.buffers = buffers.length === 0 ? [{ byteLength: bin.byteLength }] : buffers.map((b, i) => (i === 0 ? { ...b, byteLength: bin.byteLength } : b));
  return { glb: buildGlb(json, bin), clones };
}
